import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { AccountChip } from "../components/AccountChip";
import { FamilySwitcher } from "../components/FamilySwitcher";
import { Frame } from "../components/Frame";
import { NavItems, NavOffsite } from "../components/Nav";
import { Placeholder } from "../components/Placeholder";
import { usePanel } from "../panelContext";
import { FAMILY_SECTIONS, FAMILY_SECTIONS_HEADING, PLAN_SECTION, type Section } from "../sections";
import { Activity } from "./Activity";
import { Memory } from "./Memory";
import { Plate } from "./Plate";
import { Roster } from "./Roster";

/**
 * `/families/:familyId/…` — one family (ADR 0021 decision 2).
 *
 * **The id in the route is the whole of the selection, and none of the verification.** This
 * component checks it against the families the server said the caller is in, which is a
 * *rendering* decision: it decides which nav to draw and which family to name. Every request a
 * screen makes carries the same id and is verified again server-side, and a member removed
 * between the two is refused there. A panel that showed a family is not what made it readable.
 *
 * **An id that is not one of theirs sends them to the root rather than to an error.** It is
 * the ordinary shape of a stale bookmark, a link from someone else's family, or the family
 * they were in until this morning — and the root already knows how to answer all three,
 * because it is the thing that picks a family or explains why it cannot.
 */
export function FamilyScope() {
  const { familyId } = useParams<{ familyId: string }>();
  const { families, email, signOut } = usePanel();

  const known = families.some((family) => family.familyId === familyId);
  if (familyId === undefined || !known) return <Navigate to="/" replace />;

  return (
    <Frame
      scope={<FamilySwitcher families={families} familyId={familyId} />}
      account={<AccountChip email={email} onSignOut={signOut} />}
      nav={
        <>
          <NavItems
            base={`/families/${familyId}`}
            heading={FAMILY_SECTIONS_HEADING}
            sections={FAMILY_SECTIONS}
          />
          <NavOffsite />
        </>
      }
    >
      <Routes>
        {/* The section is where a member is; the family root is not a screen of its own, and
            a dashboard summarising four sections nobody has built would be four more places
            to be wrong. */}
        <Route index element={<Navigate to={FAMILY_SECTIONS[0]?.path ?? ""} replace />} />
        {FAMILY_SECTIONS.map((section) => (
          <Route key={section.path} path={section.path} element={screenFor(section, familyId)} />
        ))}
        {/* Routable but not in the nav — the roster's plan row is the one thing that raises
            it. See `PLAN_SECTION`: it is a placeholder because pricing is a decision before
            it is a screen (ADR 0021 decision 11). */}
        <Route path={PLAN_SECTION.path} element={screenFor(PLAN_SECTION, familyId)} />
        <Route path="*" element={<Navigate to={FAMILY_SECTIONS[0]?.path ?? ""} replace />} />
      </Routes>
    </Frame>
  );
}

/**
 * The screen behind a section, or the placeholder while it is somebody else's issue.
 *
 * **Keyed on `waitingOn` being absent rather than on a list of built paths**, so a section and
 * its screen are turned on by one edit in `sections.ts` — a second list here is how a nav entry
 * comes to point at a placeholder for a screen that exists. A section marked built with nothing
 * to render falls through to the placeholder, which is the safe direction: it says the page is
 * not ready, which is true.
 */
function screenFor(section: Section, familyId: string) {
  if (section.waitingOn !== undefined) return <Placeholder section={section} />;
  // **`key` is the family boundary, and it is load-bearing.** Without it React keeps the same
  // component identity across a switch, so an open draft, a pending proposal and the loaded
  // page survive into a family they do not belong to — and submitting that draft calls
  // `saveFact` with the NEW `familyId`, writing one family's words into another. Remounting
  // is also what strands the previous family's in-flight reads: their `live` flag is torn
  // down with the effect, so a late page cannot append onto the new family's screen. It
  // matters at least as much on the roster, where an open proposal names a member id.
  if (section.path === "memory") return <Memory key={familyId} familyId={familyId} />;
  if (section.path === "plate") return <Plate key={familyId} familyId={familyId} />;
  if (section.path === "roster") return <Roster key={familyId} familyId={familyId} />;
  if (section.path === "activity") return <Activity key={familyId} familyId={familyId} />;
  return <Placeholder section={section} />;
}
